import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useQueryClient } from 'react-query';

import {
  getAllUsers,
  getEssZones,
  getTesZones,
  getTgsZones,
  getUserProfileDataService,
} from 'shared/services';
import {
  handleEssSwitch,
  selectEssSwitch,
} from 'shared/slice/essSwitchSlice';
import {
  handleTgsSwitch,
  selectTgsSwitch,
} from 'shared/slice/tgsSwitchSlice';
import {
  handleTesSwitch,
  selectTesSwitch,
} from 'shared/slice/tesSwitchSlice';
import {
  handleAccessToken,
  handleAllUsers,
  selectUserInfo,
} from 'shared/slice/userSlice';
import {
  handleOpenMasterControl,
} from 'shared/slice/MCIsExpandedSlice';
import {
  buildAllLocations,
  buildSpecificLocations,
  EMPTY_LOCATIONS,
  extractLocationData,
  filterZonesWithDevices,
} from 'shared/helpers/locationsCache';
import { LOCATIONS_KEY } from 'shared/hooks/useLocations';
import { ssrStateKey } from 'shared/hooks/useSsrState';
import { mergeSsrStateIntoFlat, EMPTY_SSR_STATE } from 'shared/helpers/ssrCache';
import {
  handleEssFaults,
  handleMessagesFaults,
  handleTesFaults,
  handleTgsFaults,
} from 'shared/slice/FaultsSlice';
import {
  handleEssFCSelect,
  handleSysFCSelect,
  handleTesFCSelect,
  handleTgsFCSelect,
} from 'shared/slice/forceAndCommandsSlice';
import {
  handleUnitSelection,
  selectUnits,
} from 'shared/slice/unitsSlice';
import {
  handleEssAdminSelect,
  handleSysAdminSelect,
  handleTesAdminSelect,
  handleTgsAdminSelect,
} from 'shared/slice/adminSlice';
import { handleEssDataConsumptionLocation } from 'shared/slice/essDataConsumptionSlice';
import { handleTgsDataConsumptionLocation } from 'shared/slice/tgsDataConsumptionSlice';
import { handleTesDataConsumptionLocation } from 'shared/slice/tesDataConsumptionSlice';
import { useSetZoneOpeningsState } from 'shared/hooks/ess_tgs_tes_hooks/useSetZoneOpeningsState';
import { useSocket } from 'shared/hooks/useSocket';

export const useBootstrapSystemsData = (isMobile) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const storedAccessToken = localStorage.getItem('access_token');

  const userInfo = useSelector(selectUserInfo);
  const { isEssSwitch, isTesSwitch, isTgsSwitch, accessToken, user } = userInfo;

  const { essSwitch: essSwitches, flatEssSwitch } =
    useSelector(selectEssSwitch);
  const { tgsSwitch: tgsSwitches, flatTgsSwitch } =
    useSelector(selectTgsSwitch);
  const { tesSwitch: tesSwitches, flatTesSwitch } =
    useSelector(selectTesSwitch);
  const { isF } = useSelector(selectUnits);

  useSocket(user.user_id, accessToken);

  useEffect(() => {
    if (storedAccessToken) {
      dispatch(handleAccessToken(storedAccessToken));
      dispatch(getUserProfileDataService());
      getAllUsers().then((data) => {
        dispatch(handleAllUsers(data));
      });
    }
  }, [dispatch, storedAccessToken]);

  useEffect(() => {
    const choosenUnit = user?.temperature_unit === 'f' ? 0 : 1;
    dispatch(handleUnitSelection(choosenUnit));
  }, [dispatch, user?.temperature_unit]);

  useEffect(() => {



    const readSsr = (mac) =>
      queryClient.getQueryData(ssrStateKey(mac)) || EMPTY_SSR_STATE;
    dispatch(
      handleMessagesFaults({
        flatEssSwitch: mergeSsrStateIntoFlat(flatEssSwitch, readSsr),
        flatTgsSwitch,
        flatTesSwitch: mergeSsrStateIntoFlat(flatTesSwitch, readSsr),
      })
    );
  }, [dispatch, queryClient, flatEssSwitch, flatTgsSwitch, flatTesSwitch]);

  useSetZoneOpeningsState(essSwitches, isEssSwitch, null, 'ess', true);
  useSetZoneOpeningsState(tgsSwitches, isTgsSwitch, null, 'tgs', true);
  useSetZoneOpeningsState(tesSwitches, isTesSwitch, null, 'tes', true);

  useEffect(() => {
    if (!isMobile) return;

    if (isEssSwitch) {
      dispatch(handleOpenMasterControl({ swtName: 'ess', status: false }));
    }
    if (isTgsSwitch) {
      dispatch(handleOpenMasterControl({ swtName: 'tgs', status: false }));
    }
    if (isTesSwitch) {
      dispatch(handleOpenMasterControl({ swtName: 'tes', status: false }));
    }
  }, [dispatch, isEssSwitch, isMobile, isTesSwitch, isTgsSwitch]);

  const {
    data: essZones,
    isLoading: essZonesLoading,
    refetch: essRefetch,
  } = useQuery('essZones', () => getEssZones({ structured: true }).then(filterZonesWithDevices), {
    enabled: !!accessToken,
    staleTime: Infinity,
  });

  const {
    data: tgsZones,
    isLoading: tgsZonesLoading,
    refetch: tgsRefetch,
  } = useQuery('tgsZones', () => getTgsZones({ structured: true }).then(filterZonesWithDevices), {
    enabled: !!accessToken,
    staleTime: Infinity,
  });

  const {
    data: tesZones,
    isLoading: tesZonesLoading,
    refetch: tesRefetch,
  } = useQuery('tesZones', () => getTesZones({ structured: true }).then(filterZonesWithDevices), {
    enabled: !!accessToken,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (essZones) {
      dispatch(handleEssSwitch({ essZones, isF }));
      queryClient.setQueryData(LOCATIONS_KEY, (prev = EMPTY_LOCATIONS) => ({
        ...prev,
        ess: extractLocationData(essZones),
      }));
      dispatch(handleEssFaults(essZones));
      dispatch(handleEssFCSelect(essZones));
      dispatch(handleEssAdminSelect(essZones));
      dispatch(handleEssDataConsumptionLocation(essZones));
    }
    if (tgsZones) {
      dispatch(handleTgsSwitch({ tgsZones, isF }));
      queryClient.setQueryData(LOCATIONS_KEY, (prev = EMPTY_LOCATIONS) => ({
        ...prev,
        tgs: extractLocationData(tgsZones),
      }));
      dispatch(handleTgsFaults(tgsZones));
      dispatch(handleTgsFCSelect(tgsZones));
      dispatch(handleTgsAdminSelect(tgsZones));
      dispatch(handleTgsDataConsumptionLocation(tgsZones));
    }
    if (tesZones) {
      dispatch(handleTesSwitch({ tesZones, isF }));
      queryClient.setQueryData(LOCATIONS_KEY, (prev = EMPTY_LOCATIONS) => ({
        ...prev,
        tes: extractLocationData(tesZones),
      }));
      dispatch(handleTesFaults(tesZones));
      dispatch(handleTesFCSelect(tesZones));
      dispatch(handleTesAdminSelect(tesZones));
      dispatch(handleTesDataConsumptionLocation(tesZones));
    }
    if (essZones && tgsZones && tesZones) {
      const combinedZones = [...tesZones, ...tgsZones, ...essZones];
      queryClient.setQueryData(LOCATIONS_KEY, (prev = EMPTY_LOCATIONS) => ({
        ...prev,
        all: buildAllLocations(combinedZones),
        specific: buildSpecificLocations(combinedZones),
      }));
      dispatch(handleSysFCSelect([...tesZones, ...tgsZones, ...essZones]));
      dispatch(handleSysAdminSelect([...tesZones, ...tgsZones, ...essZones]));
    }
  }, [dispatch, essZones, isF, tesZones, tgsZones, queryClient]);






  return {
    accessToken,
    storedAccessToken,
    isDataLoading:
      !!accessToken && (essZonesLoading || tgsZonesLoading || tesZonesLoading),
    essRefetch,
    tgsRefetch,
    tesRefetch,
  };
};
